/**
 * Value encoding / decoding for taxonomy-based PLP route URLs.
 *
 * These two functions are exact inverses (modulo the `+`/`%2B` distinction
 * described below). They operate on a single decoded value at a time — splitting
 * a segment on `;` and joining with `;` is the caller's job.
 */

/**
 * Characters that are valid, unencoded, inside a URL path segment for this
 * scheme. This is the RFC 3986 `pchar` set
 *
 *   pchar = unreserved / sub-delims / ":" / "@"
 *
 * minus the sub-delims this scheme reserves or otherwise encodes:
 * `;` (multi-value), `,` (legacy multi-value), `+` (space), `&` and `'`
 * (apostrophe, encoded as `%27`). A literal space is handled separately
 * (encoded as `+`), so it is intentionally absent here.
 */
const SAFE_SEGMENT_CHAR = /[A-Za-z0-9\-._~!$()*=:@]/;

/**
 * Encode a single decoded value for use in a path segment.
 *
 *  - a space becomes `+`;
 *  - an apostrophe becomes `%27` (handled explicitly because
 *    `encodeURIComponent` leaves `'` untouched);
 *  - characters in {@link SAFE_SEGMENT_CHAR} pass through verbatim;
 *  - everything else (including `/`, `?`, `#`, `%`, `&` and the reserved
 *    delimiters `;`, `,` and a literal `+`) is percent-encoded as its UTF-8
 *    byte sequence.
 *
 * Casing is preserved.
 */
export const encodeValue = (value: string): string =>
  Array.from(value)
    .map((char) => {
      if (char === ' ') {
        return '+';
      }
      if (char === "'") {
        return '%27';
      }
      if (SAFE_SEGMENT_CHAR.test(char)) {
        return char;
      }
      return encodeURIComponent(char);
    })
    .join('');

/**
 * Decode a single value from a path segment: substitute `+`→space first, then
 * percent-decode. The inverse of {@link encodeValue}.
 *
 * @throws {URIError} if the value contains a malformed percent-escape.
 */
export const decodeValue = (value: string): string => decodeURIComponent(value.replace(/\+/g, ' '));
